import { SmartAudienceKind, SmartAudienceListItemDto } from "@/models/club";
import { faNumber } from "@/modules/mobileHome/format";

// Persian verbs agree with the count: "۱ مشتری … نیامده" but "۵ مشتری … نیامده‌اند".
const plural = (count: number, verb: string) => (count === 1 ? verb : `${verb}‌اند`);

const serviceName = (audience: SmartAudienceListItemDto) => audience.service?.title?.trim() || "خدمت بعدی";

// Evergreen audiences (kinds 12–19): offered to every salon, history or not, after the data-driven ones.
// Plain numbers until the API client is regenerated from a backend that knows them.
export const EVERGREEN_KINDS = {
  openSlots: 12,
  announcement: 13,
  specialOffer: 14,
  welcomeNew: 15,
  businessUpdate: 16,
  greeting: 17,
  women: 18,
  men: 19,
} as const;

// `yours` is «‌تان» in a personal scope.
const evergreen: Record<number, { title: (n: string, yours: string) => string; action: string; label: string }> = {
  [EVERGREEN_KINDS.openSlots]: {
    title: (n, yours) => `وقت‌های خالی این هفته را به ${n} مشتری${yours} خبر بدهید`,
    action: "اطلاع‌رسانی با پیامک",
    label: "وقت خالی این هفته",
  },
  [EVERGREEN_KINDS.announcement]: {
    title: (n, yours) => `خدمت جدید را به ${n} مشتری${yours} معرفی کنید`,
    action: "معرفی با پیامک",
    label: "معرفی خدمت جدید",
  },
  [EVERGREEN_KINDS.specialOffer]: {
    title: (n, yours) => `یک پیشنهاد ویژه برای ${n} مشتری${yours}`,
    action: "ارسال پیشنهاد",
    label: "پیشنهاد ویژه",
  },
  [EVERGREEN_KINDS.welcomeNew]: {
    title: (n, yours) => `به ${n} مشتری جدید${yours} خوش‌آمد بگویید`,
    action: "خوش‌آمدگویی با پیامک",
    label: "مشتریان جدید این ماه",
  },
  [EVERGREEN_KINDS.businessUpdate]: {
    title: (n, yours) => `ساعت کاری یا تعطیلی را به ${n} مشتری${yours} اطلاع دهید`,
    action: "اطلاع‌رسانی با پیامک",
    label: "اطلاع‌رسانی",
  },
  [EVERGREEN_KINDS.greeting]: {
    title: (n, yours) => `تبریک مناسبت به ${n} مشتری${yours}`,
    action: "تبریک با پیامک",
    label: "تبریک مناسبت",
  },
  [EVERGREEN_KINDS.women]: { title: (n) => `پیام ویژه برای ${n} مشتری خانم`, action: "ارسال پیامک", label: "مشتریان خانم" },
  [EVERGREEN_KINDS.men]: { title: (n) => `پیام ویژه برای ${n} مشتری آقا`, action: "ارسال پیامک", label: "مشتریان آقا" },
};

export const isEvergreen = (kind?: number) => kind !== undefined && kind in evergreen;

// The sentence on a hint. `personal`: the stylist's own customers («مشتری‌تان»), not the whole salon's.
export function audienceTitle(audience: SmartAudienceListItemDto, personal = false) {
  const count = audience.customerCount ?? 0;
  const n = faNumber(count);
  const customer = `مشتری${personal ? "‌تان" : ""}`;
  const fixed = audience.kind !== undefined ? evergreen[audience.kind] : undefined;
  if (fixed) return fixed.title(n, personal ? "‌تان" : "");
  switch (audience.kind) {
    case SmartAudienceKind.Value1:
      return `وقت ${serviceName(audience)} ${n} ${customer} رسیده`;
    case SmartAudienceKind.Value2:
      return `${n} مشتری ثابت${personal ? "‌تان" : ""} بیش از ۶ هفته است که ${plural(count, "نیامده")}`;
    case SmartAudienceKind.Value3:
      return `تولد ${n} ${customer} در این هفته است`;
    case SmartAudienceKind.Value4:
      return `${n} مشتری جدید${personal ? "‌تان" : ""} هنوز برای بار دوم ${plural(count, "نیامده")}`;
    case SmartAudienceKind.Value5:
      return count === 1
        ? "۱ نفر نوبتش را لغو کرد و دوباره رزرو نکرد"
        : `${n} نفر نوبتشان را لغو کردند و دوباره رزرو نکردند`;
    case SmartAudienceKind.Value6:
      return `${n} ${customer} بیش از ${faNumber(audience.days ?? 60)} روز است که ${plural(count, "نیامده")}`;
    case SmartAudienceKind.Value7:
      return `فردا وقت خالی دارید؛ ${n} مشتری ${count === 1 ? "آماده‌ی رزرو است" : "آماده‌ی رزروند"}`;
    case SmartAudienceKind.Value8:
      return `سالگرد ازدواج ${n} ${customer} در این هفته است`;
    case SmartAudienceKind.Value9:
      return `${n} ${customer} بیش از ۴ ماه است که ${plural(count, "نیامده")}`;
    case SmartAudienceKind.Value10:
      return `${n} مخاطب هنوز هیچ نوبتی ${plural(count, "نگرفته")}`;
    default:
      return `${n} ${customer} برای مناسبت پیش رو`;
  }
}

// The call to action under the sentence.
export function audienceAction(kind?: SmartAudienceKind) {
  if (kind !== undefined && evergreen[kind]) return evergreen[kind].action;
  switch (kind) {
    case SmartAudienceKind.Value1:
      return "یادآوری با پیامک";
    case SmartAudienceKind.Value3:
    case SmartAudienceKind.Value8:
      return "تبریک با پیامک";
    case SmartAudienceKind.Value4:
      return "دعوت به نوبت دوم";
    case SmartAudienceKind.Value5:
      return "پیشنهاد نوبت جدید";
    case SmartAudienceKind.Value7:
      return "پر کردن وقت‌های فردا";
    case SmartAudienceKind.Value10:
      return "دعوت به اولین نوبت";
    case SmartAudienceKind.Value11:
      return "اطلاع‌رسانی با پیامک";
    default:
      return "دعوت دوباره با پیامک";
  }
}

// A short name for the audience in the campaign summary: "۱۲ نفر · وقت ترمیم ناخن".
export function audienceLabel(audience: SmartAudienceListItemDto, excluded = 0) {
  const names: Partial<Record<SmartAudienceKind, string>> = {
    [SmartAudienceKind.Value1]: `وقت ${serviceName(audience)}`,
    [SmartAudienceKind.Value2]: "مشتریان ثابتی که دیر کرده‌اند",
    [SmartAudienceKind.Value3]: "تولد این هفته",
    [SmartAudienceKind.Value4]: "مشتریان جدیدی که برنگشته‌اند",
    [SmartAudienceKind.Value5]: "لغو بدون رزرو دوباره",
    [SmartAudienceKind.Value6]: "مشتریان غایب",
    [SmartAudienceKind.Value7]: "پر کردن وقت فردا",
    [SmartAudienceKind.Value8]: "سالگرد ازدواج",
    [SmartAudienceKind.Value9]: "مشتریان از دست‌رفته",
    [SmartAudienceKind.Value10]: "بدون هیچ نوبت",
    [SmartAudienceKind.Value11]: "مناسبت پیش رو",
  };
  const fixed = audience.kind !== undefined ? evergreen[audience.kind] : undefined;
  if (fixed) names[audience.kind!] = fixed.label;
  const count = Math.max(0, (audience.customerCount ?? 0) - excluded);
  return `${faNumber(count)} نفر · ${(audience.kind && names[audience.kind]) || "پیشنهاد هوشمند"}`;
}

// "آخرین ارسال: ۵ روز پیش" while the last send is under two weeks old.
export function lastSentNote(lastSentAt?: string | null, now = new Date()) {
  if (!lastSentAt) return undefined;
  const days = Math.floor((now.getTime() - new Date(lastSentAt).getTime()) / 86_400_000);
  if (Number.isNaN(days) || days >= 14) return undefined;
  return days <= 0 ? "آخرین ارسال: امروز" : `آخرین ارسال: ${faNumber(days)} روز پیش`;
}

// Where a hint leads: the «متن پیام» step with this audience already chosen.
export const audiencePath = (key: string, merchantUserId?: number) =>
  `/dashboard/messages/selectTemplate?audience=${encodeURIComponent(key)}${merchantUserId ? `&member=${merchantUserId}` : ""}`;

// Audiences where a discount gives the extra push: people who stopped coming, came once, cancelled,
// have a birthday, or a week with free slots / an offer.
const DISCOUNT_KINDS = new Set<number>([2, 3, 4, 5, 6, EVERGREEN_KINDS.openSlots, EVERGREEN_KINDS.specialOffer, EVERGREEN_KINDS.welcomeNew]);
export const offersDiscount = (kind?: number) => kind !== undefined && DISCOUNT_KINDS.has(kind);

// The discount SMS flow at its text step, with this audience already chosen.
export const audienceDiscountPath = (key: string, merchantUserId?: number) =>
  `/dashboard/discount/selectMessage?audience=${encodeURIComponent(key)}${merchantUserId ? `&member=${merchantUserId}` : ""}`;

// Filled in per customer at send time, so an empty preview is no reason to block the template.
export const PER_RECIPIENT_PLACEHOLDERS = ["ServiceTitle", "DaysSinceLastVisit", "LastStylistName"];

// Sample values for those placeholders in a preview, where the server sends none («مقدار وارد نشده»
// would be wrong: each customer gets their own).
export function withRecipientSamples<T extends { key?: string | null; preView?: string | null }>(tags: T[], serviceTitle?: string | null): T[] {
  const samples: Record<string, string> = { ServiceTitle: serviceTitle?.trim() || "ترمیم ناخن", DaysSinceLastVisit: "۲۴", LastStylistName: "سارا" };
  return tags.map((tag) => {
    const name = PER_RECIPIENT_PLACEHOLDERS.find((key) => tag.key?.includes(key));
    return name && !tag.preView ? { ...tag, preView: samples[name] } : tag;
  });
}
